import _ from 'lodash';
import { Reducer } from 'redux';
import {
	AudienceSourceType,
	DistributionType,
	IHierarchyMetadata,
	IHrisAttributeModel,
	IHrisFileSummary,
	IPersonAttribute,
} from '../models';
import { IBreakoutModelVm } from '../models/IBreakoutModel';
import * as HierarchyAPI from '../services/HierarchyAPI';
import * as PersonAttributeAPI from '../services/PersonAttributeAPI';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { TThunkAction } from './';
import {
	IParticipationMonitorAttributesGetFailed,
	IParticipationMonitorAttributesGetRequested,
	IParticipationMonitorAttributesGetSuccessful,
	IParticipationMonitorHrisAttributesGetFailed,
	IParticipationMonitorHrisAttributesGetRequested,
	IParticipationMonitorHrisAttributesGetSuccessful,
	IParticipationMonitorHrisFileSummaryGetFailed,
	IParticipationMonitorHrisFileSummaryGetRequested,
	IParticipationMonitorHrisFileSummaryGetSuccessful,
	IParticipationMonitorNoSource,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IBreakoutState {
	breakoutsLoading: boolean;
	availableBreakouts: IBreakoutModelVm;
	hrisExists: boolean;
	hrisFileName: string;
}

export const defaultState: IBreakoutState = {
	breakoutsLoading: true,
	availableBreakouts: { attributes: [], hierarchies: [] },
	hrisExists: false,
	hrisFileName: '',
};

export type TActionCreator<R> = TThunkAction<R, KnownActions>;

export const ActionCreators = {
	GetAvailableAttributes:
		(surveyId: number, audienceId: number, clientId: number, distributionType?: DistributionType): TActionCreator<void> =>
		(dispatch, getState) => {
			const {
				audienceSourceType,
				externalHrisId: hrisId,
				externalHrisFileName: hrisFileName,
			} = getState().survey.surveyEditModel.audienceSource;

			if (audienceSourceType === AudienceSourceType.FileUpload) {
				if (audienceId > 0 && distributionType !== undefined && DistributionHelper.isNotAnonymous(distributionType)) {
					dispatch({ type: ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_REQUESTED });

					return PersonAttributeAPI.getPersonAttributes(surveyId, audienceId, clientId)
						.then((attributes: IPersonAttribute[]) => {
							const uniqueNames: string[] = [];
							attributes.forEach((attr: IPersonAttribute) => {
								if (!uniqueNames.find((an) => an === attr.attributeName)) {
									uniqueNames.push(attr.attributeName);
								}
							});
							dispatch({ type: ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_SUCCEEDED, payload: uniqueNames });
						})
						.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_FAILED }));
				}
			} else if (audienceSourceType === AudienceSourceType.Hris && hrisId !== undefined) {
				dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_REQUESTED });

				return HierarchyAPI.getHrisFileSummary(hrisId)
					.then((hrisSummary: IHrisFileSummary) => {
						dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_SUCCEEDED, payload: true });

						dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_REQUESTED });

						const arrayOfPromises: any[] = [];

						let availableAttributes: string[] = [];
						let availableHierarchies: IHierarchyMetadata[] = [];
						let error = false;

						const hiearchyAttributesPromise = HierarchyAPI.getHrisAttributes(hrisId)
							.then((attributes: IHrisAttributeModel[]) => {
								availableAttributes = _.map(attributes, (attribute) => attribute.name);
							})
							.catch(() => (error = true));
						arrayOfPromises.push(hiearchyAttributesPromise);

						const attributesPromise = HierarchyAPI.getHrisHierarchies(hrisId)
							.then((hierarchies: IHierarchyMetadata[]) => {
								availableHierarchies = hierarchies;
							})
							.catch(() => (error = true));
						arrayOfPromises.push(attributesPromise);

						return Promise.all(arrayOfPromises)
							.then(() => {
								if (error === true) {
									dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_FAILED });
								} else {
									dispatch({
										type: ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_SUCCEEDED,
										payload: { hierarchies: availableHierarchies, attributes: availableAttributes },
									});
								}
							})
							.catch(() => {
								dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_FAILED });
							});
					})
					.catch(() => dispatch({ type: ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_FAILED, payload: hrisFileName! }));
			}

			dispatch({ type: ActionTypes.REPORTING_SETUP_PARTICIPANT_NO_SOURCE });
			return Promise.resolve();
		},
};

type KnownActions =
	| IParticipationMonitorAttributesGetRequested
	| IParticipationMonitorAttributesGetSuccessful
	| IParticipationMonitorAttributesGetFailed
	| IParticipationMonitorHrisAttributesGetRequested
	| IParticipationMonitorHrisAttributesGetSuccessful
	| IParticipationMonitorHrisAttributesGetFailed
	| IParticipationMonitorHrisFileSummaryGetRequested
	| IParticipationMonitorHrisFileSummaryGetSuccessful
	| IParticipationMonitorHrisFileSummaryGetFailed
	| IParticipationMonitorNoSource;

export const reducer: Reducer<IBreakoutState, KnownActions> = (
	state: IBreakoutState = defaultState,
	action: KnownActions,
): IBreakoutState => {
	switch (action.type) {
		case ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_FAILED:
		case ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_FAILED:
		case ActionTypes.REPORTING_SETUP_PARTICIPANT_NO_SOURCE:
			return { ...state, breakoutsLoading: false };
		case ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_FAILED:
			return { ...state, breakoutsLoading: false, hrisFileName: action.payload };
		case ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_REQUESTED:
		case ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_REQUESTED:
		case ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_REQUESTED:
			return { ...state, breakoutsLoading: true };
		case ActionTypes.REPORTING_SETUP_PARTICIPANT_ATTRIBUTES_GET_SUCCEEDED:
			return {
				...state,
				breakoutsLoading: false,
				availableBreakouts: { ...state.availableBreakouts, attributes: action.payload },
			};
		case ActionTypes.REPORTING_SETUP_HRIS_ATTRIBUTES_GET_SUCCEEDED:
			return {
				...state,
				breakoutsLoading: false,
				availableBreakouts: { hierarchies: action.payload.hierarchies, attributes: action.payload.attributes },
			};
		case ActionTypes.REPORTING_SETUP_HRIS_FILE_SUMMARY_GET_SUCCEEDED:
			return {
				...state,
				breakoutsLoading: false,
				hrisExists: action.payload,
			};
	}
	return state;
};
